/**
 * Document Forms (obj_type `E`) — the platform's transport.
 *
 * Transport ONLY. Every decision about what a document does lives in
 * `@twasta/page-runtime/er` and in the engine's `er` package, so the deployed
 * standalone runtime implements this same interface over its own base URL and
 * renders the identical record. A behaviour that leaked into this file would be
 * a behaviour the downloaded app does not have.
 */

import api from './client'
import type {
  DocumentCloseResult, DocumentFormRow, DocumentFormRuntime,
  DocumentFormSession, DocumentRun, DocumentSaveResult,
} from '@twasta/page-runtime/er'
import { makeDesignerTxnRuntime } from '@/livepreview/runtime/designerTxnRuntime'

export interface DocumentFormSummary {
  obj_name: string
  title: string
  description?: string
  governance?: 'none' | 'audited' | 'regulated'
  /** The PRIMARY transaction — what the Document view is offered on. */
  anchor_obj?: string
  /** Every transaction the document covers (§4A), primary first. */
  transactions?: {
    obj_name: string; alias?: string; role?: string; read_only?: boolean
    kind?: 'transaction' | 'visual'
  }[]
  /** What the primary IS — a transaction (records) or a visual (a result set). */
  anchor_kind?: 'transaction' | 'visual'
  keys?: string[]
  /**
   * The keys that PARTITION a run into documents — `source: "row"`.
   *
   * Empty on a transaction form, and on a visual form that makes one document
   * per opening. On a visual form with an identity it is what turns one run
   * into one document per customer, per entity, per order.
   */
  identity?: string[]
  capture_mode?: string
  templates?: number
  /**
   * Structural problems with the form (§4A) — a `blocking` one makes it
   * impossible to open. Listed rather than hidden: a form that looks fine here
   * and fails on the shop floor is the worst version of this screen.
   */
  problems?: { code: string; message: string; blocking?: boolean }[]
  /** Present when the model file's governance claim does not hold — see
   *  `er/model.py::list_forms`, which lists a broken form rather than hiding
   *  the one somebody has to go and fix. */
  error?: string
}

export async function listDocumentForms(project: string)
  : Promise<DocumentFormSummary[]> {
  const res = await api.post('/er/list', { project })
  return res.data?.forms || []
}

/** Every document form anchored on one transaction — what gates the switcher. */
export async function documentFormsForObject(project: string, objName: string)
  : Promise<DocumentFormSummary[]> {
  const res = await api.post('/er/for-object', { project, obj_name: objName })
  return res.data?.forms || []
}

export async function loadDocumentForm(project: string, objName: string) {
  const res = await api.post('/er/model', { project, obj_name: objName })
  return (res.data?.content || res.data?.default) as Record<string, unknown>
}

export async function saveDocumentForm(
  project: string, objName: string, model: Record<string, unknown>,
) {
  const res = await api.post('/er/save-model',
    { project, obj_name: objName, model })
  return res.data
}

export async function seedDocumentFormTemplates(project: string, objName: string) {
  const res = await api.post('/er/seed-templates',
    { project, obj_name: objName })
  return res.data
}

export async function previewDocumentForm(args: {
  project: string
  objName: string
  txnId?: string
  form?: Record<string, unknown>
  templateKey?: string
}) {
  const res = await api.post('/er/preview', {
    project: args.project, obj_name: args.objName, txn_id: args.txnId,
    form: args.form, template_key: args.templateKey,
  })
  return res.data
}

/**
 * Everything wrong with the form as it stands — the designer's readiness rail.
 *
 * Server-side on purpose. The same `er.validate.problems` answers here, at the
 * publish gate and in the runtime's `open_document`, so the rail can never say
 * a form is ready when opening it would refuse. A copy of the rules in the
 * browser is a copy that drifts, and it would drift in the direction of saying
 * yes.
 */
export async function documentFormProblems(
  project: string, objName: string, form?: Record<string, unknown>,
): Promise<{ code: string; message: string; blocking?: boolean }[]> {
  const res = await api.post('/er/problems',
    { project, obj_name: objName, form })
  return res.data?.problems || []
}

/**
 * The runtime a `<DocumentFormView>` takes, bound to one project + form.
 *
 * Built per (project, form) rather than per render so the view's `useEffect`
 * on it does not re-open the document on every keystroke.
 */
export function documentFormRuntime(project: string, formObj: string)
  : DocumentFormRuntime {
  return {
    // The transaction transport, whole. A lookup column in a document opens
    // the SAME picker it opens on the grid form — the same component over the
    // same `/livepreview/lookup` endpoint — so the document can never offer a
    // value the transaction's own save would refuse.
    txn: makeDesignerTxnRuntime(project),
    async open({ txnId, keys, templateKey, instanceId, params, rows }) {
      const res = await api.post('/er/open', {
        project, obj_name: formObj, txn_id: txnId, keys: keys || {},
        template_key: templateKey, instance_id: instanceId,
        params: params || {}, rows: rows ?? null,
      })
      return res.data as DocumentFormSession
    },
    async run({ params } = {}) {
      const res = await api.post('/er/run', {
        project, obj_name: formObj, params: params || {},
      })
      return res.data as DocumentRun
    },
    async resolve({ txnId, keys, values, params }) {
      const res = await api.post('/er/resolve', {
        project, obj_name: formObj, txn_id: txnId, keys: keys || {}, values,
        params: params || {},
      })
      return res.data
    },
    async save({ txnId, keys, txnIds, values, tables, ignoreWarnings,
                 justifications, instanceId, narrative, reason, params }) {
      const res = await api.post('/er/save', {
        project, obj_name: formObj, txn_id: txnId,
        keys: keys || {}, txn_ids: txnIds || {}, params: params || {},
        values, tables: tables as Record<string, DocumentFormRow[]>,
        ignore_warnings: ignoreWarnings || [],
        // The written reason for each of those, which a governed document
        // records as a deviation and refuses the acknowledgement without.
        justifications: justifications || {},
        instance_id: instanceId || '', narrative: narrative || {},
        // 21 CFR 11.10(e). Empty on every ordinary save; the engine only asks
        // once a reading has been signed for.
        reason: reason || '',
      })
      return res.data as DocumentSaveResult
    },
    async closeSection({ instanceId, sectionId, values, tables, narrative,
                         body }) {
      const res = await api.post('/er/sections/close', {
        project, obj_name: formObj, instance_id: instanceId,
        section_id: sectionId,
        values, tables: tables as Record<string, DocumentFormRow[]>,
        narrative: narrative || {},
        body: body ?? undefined,
      })
      return res.data
    },
    async reviseSection({ instanceId, sectionId, reason }) {
      const res = await api.post('/er/sections/revise', {
        project, obj_name: formObj, instance_id: instanceId,
        section_id: sectionId, reason,
      })
      return res.data
    },
    async listSections(instanceId) {
      const res = await api.post('/er/sections', {
        project, obj_name: formObj, instance_id: instanceId,
      })
      return res.data?.sections || []
    },
    async close({ instanceId, txnId, keys, values, tables, narrative, body }) {
      const res = await api.post('/er/close', {
        project, obj_name: formObj, instance_id: instanceId, txn_id: txnId,
        keys: keys || {}, values,
        tables: tables as Record<string, DocumentFormRow[]>,
        narrative: narrative || {},
        body: body ?? undefined,
      })
      return res.data as DocumentCloseResult
    },
  }
}
